/**
 * @flow
 * */
import { put, takeLatest, takeEvery } from 'redux-saga/effects';

import riskWeeklyConfigSDK from 'modules/RiskWeeklyReport/core/sdk/RiskWeeklyConfigSDK';
import RISK_WEEKLY_ACTION_TYPES from 'modules/RiskWeeklyReport/store/actions/types';
import {
  setFetchConfigOverviewSuccess,
  setFetchConfigOverviewFailure,
  setFetchConfigPageSuccess,
  setFetchConfigPageFailure,
  setFetchConfigUnmappedSuccess,
  setFetchConfigUnmappedFailure,
  setSaveConfigRowSuccess,
  setSaveConfigRowFailure,
  setDeleteConfigRowSuccess,
  setDeleteConfigRowFailure,
  setFetchConfigHistorySuccess,
  setFetchConfigHistoryFailure
} from 'modules/RiskWeeklyReport/store/actions';

class RiskWeeklyConfigController {
  static* fetchConfigOverview() {
    try {
      const data = yield riskWeeklyConfigSDK.getOverview();
      yield put(setFetchConfigOverviewSuccess({ data }));
    } catch (err) {
      // An aborted call is a superseded request, not a failure: reporting it would flash an
      // error every time the operator switches tab quickly.
      if (err.name === 'AbortError') return;
      yield put(setFetchConfigOverviewFailure({ error: err.message }));
    }
  }

  static* fetchConfigPage({ payload }) {
    const { key, ...query } = payload;
    try {
      const data = yield riskWeeklyConfigSDK.getPage(query);
      yield put(setFetchConfigPageSuccess({ key, data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchConfigPageFailure({ key, error: err.message }));
    }
  }

  static* fetchConfigUnmapped() {
    try {
      const data = yield riskWeeklyConfigSDK.getUnmapped();
      yield put(setFetchConfigUnmappedSuccess({ data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchConfigUnmappedFailure({ error: err.message }));
    }
  }

  static* saveConfigRow({ payload }) {
    try {
      const data = yield riskWeeklyConfigSDK.save(payload);
      // No "which list to reload" hint: the reducer marks the overview, the queue and the
      // comparison stale, and each container refetches from its own status. One mechanism beats
      // a hint the saga would have to keep in step with the screens.
      yield put(setSaveConfigRowSuccess({ data }));
    } catch (err) {
      // A write is never aborted, so every error here is real and has to reach the operator.
      yield put(setSaveConfigRowFailure({ error: err.message }));
    }
  }

  static* deleteConfigRow({ payload }) {
    try {
      const data = yield riskWeeklyConfigSDK.remove(payload);
      yield put(setDeleteConfigRowSuccess({ data }));
    } catch (err) {
      yield put(setDeleteConfigRowFailure({ error: err.message }));
    }
  }

  static* fetchConfigHistory({ payload }) {
    const { key, ...query } = payload;
    try {
      const data = yield riskWeeklyConfigSDK.getHistory(query);
      yield put(setFetchConfigHistorySuccess({ key, data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchConfigHistoryFailure({ key, error: err.message }));
    }
  }
}

export default function* riskWeeklyConfigController(): Generator<any, any, any> {
  yield takeLatest(
    RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_OVERVIEW,
    RiskWeeklyConfigController.fetchConfigOverview
  );
  // takeEvery: each area is its own list, and the SDK cancels a superseded request for the same
  // area rather than letting Partners cancel Employees.
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_PAGE,
    RiskWeeklyConfigController.fetchConfigPage
  );
  yield takeLatest(
    RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_UNMAPPED,
    RiskWeeklyConfigController.fetchConfigUnmapped
  );
  // takeEvery for the writes, not takeLatest: dropping a save because a second one started
  // would lose an edit the operator believes they made.
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.SAVE_CONFIG_ROW,
    RiskWeeklyConfigController.saveConfigRow
  );
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.DELETE_CONFIG_ROW,
    RiskWeeklyConfigController.deleteConfigRow
  );
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_HISTORY,
    RiskWeeklyConfigController.fetchConfigHistory
  );
}
